import { RAG_STATUS } from 'helpers/calc-rag-status';
import mostRecentDate from 'helpers/most-recent-date';
import useCurriculumStore from '..';
import { REMEMBER_CONFIG } from '../../app-constants';

function getGoalsProgress(userState, userId, revisionId, homeworkId) {
  if (!userId) {
    const state = useCurriculumStore.getState();
    if (revisionId) {
      const revision = state.getRevision(revisionId);
      if (revision?.useRevisionProgress) {
        return revision.goalsProgress ?? {};
      }
    } else if (homeworkId) {
      const homework = state.getHomework(homeworkId);
      return homework.goalsProgress ?? {};
    }
  }
  return userId
    ? userState.pupilsGoalsProgress[userId]
    : userState.goalsProgress ?? {};
}

function getGoalProgress(userState, goalId, userId, revisionId, homeworkId) {
  return (
    getGoalsProgress(userState, userId, revisionId, homeworkId)[goalId] ?? {}
  );
}

function getStepProgress(
  userState,
  goalId,
  userId,
  step,
  revisionId,
  homeworkId
) {
  return (
    getGoalProgress(userState, goalId, userId, revisionId, homeworkId)[step] ??
    {}
  );
}

// Returns: 0 or 1
// Input: userState.goalsProgress[goalId]?.myGoal?.lastNav
function myGoalStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const progress = getStepProgress(
      userState,
      goalId,
      userId,
      'myGoal',
      revisionId,
      homeworkId
    );
    return Number(!!progress.lastNav);
  };
}

// Input:
// curriculum
//    [prereqStatus]
function readinessStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const state = useCurriculumStore.getState();
    const rags = (state.goals[goalId]?.readinessObjectives ?? [])
      .filter((o) => o.canQuiz)
      .map((o) => state.prereqStatus[o.id]);

    if (!rags.length) {
      const progress = getStepProgress(
        userState,
        goalId,
        userId,
        'readiness',
        revisionId,
        homeworkId
      );
      return progress.lastNav ? 1 : 0;
    }

    const maxPoints = rags.length;
    const points = rags.filter((rag) => rag === RAG_STATUS.GREEN).length;

    return points / maxPoints;
  };
}

function readinessQuizAttempted(goalId, type, userId, homeworkId) {
  return (userState) => {
    const progress = getStepProgress(
      userState,
      goalId,
      homeworkId,
      userId,
      'readiness'
    );
    return type === 'SPECIFIC_READINESS'
      ? progress.specificQuizAttempted
      : progress.generalQuizAttempted;
  };
}

function readinessQuizCompleted(goalId, type, userId) {
  return (userState) => {
    const progress = getStepProgress(userState, goalId, userId, 'readiness');
    return type === 'SPECIFIC_READINESS'
      ? progress.specificQuizCompleted
      : progress.generalQuizCompleted;
  };
}

function getGoalCompletedStatuses(goalIds, homeworkId) {
  return (userState) => {
    return goalIds.reduce((o, id) => {
      const curr = o || {};
      curr[id] = goalIsCompleted(
        id,
        undefined,
        undefined,
        homeworkId
      )(userState);
      return curr;
    }, {});
  };
}

// Returns: { playhead:Integer, progress:Number<Range[0, 1]>, watchedMins:Integer, durationMins:Integer }
// Input: userState.goalsProgress[goalId]?.[step]?.[videoId]
function videoProgress(goalId, step, videoId, userId, revisionId, homeworkId) {
  return (userState) => {
    const stepStatus = getStepProgress(
      userState,
      goalId,
      userId,
      step,
      revisionId,
      homeworkId
    );
    const video = stepStatus?.videos?.[videoId] ?? {};
    const { playhead, watchedPeriods, duration } = video;
    let { progress, watchedMins, durationMins } = video;

    if (!watchedPeriods) {
      return {
        playhead: 0,
        progress: 0,
      };
    }

    const watchedSecs = watchedPeriods.reduce(
      (sum, [start, end]) => sum + end - start,
      0
    );

    // Vimeo reporting of video durations not reliable/consistent.
    // Can end up with progress > 1.
    if (progress == null) {
      progress = Math.min(1, watchedSecs / duration);
    }

    // Avoid absurd visual state where it shows watched `n` of `n` mins, but incomplete.
    if (durationMins == null) {
      durationMins = Math.ceil(duration / 60);
    }

    if (watchedMins == null) {
      watchedMins = progress >= 1 ? durationMins : Math.floor(watchedSecs / 60);
    }

    return {
      playhead,
      progress,
      watchedMins,
      durationMins,
      watchedPeriods,
    };
  };
}

// Returns: Number<Range[0, 1]>
// Input:
//    curriculum
//    [videoProgress]
function videoStepProgress(goalId, step, userId, revisionId, homeworkId) {
  return (userState) => {
    const videoIds =
      useCurriculumStore.getState().goals?.[goalId]?.[`${step}VideoIds`];

    if (!videoIds) return 0;

    return (
      videoIds.reduce(
        (sum, videoId) =>
          sum +
          videoProgress(
            goalId,
            step,
            videoId,
            userId,
            revisionId,
            homeworkId
          )(userState).progress,
        0
      ) / videoIds.length
    );
  };
}

function getFirstIncompleteVideo(goalId, step, videoIds) {
  return (userState) =>
    videoIds.find(
      (videoId) =>
        videoProgress(goalId, step, videoId)(userState).progress !== 1
    );
}

// Returns: Number<Range[0, 1]>
// Input:
//    videoStepProgress
//    userState.goalsProgress[goalId]?.learn
function learnStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) =>
    videoStepProgress(
      goalId,
      'learn',
      userId,
      revisionId,
      homeworkId
    )(userState);
}

// Returns: Number<Range[0, 1]>
// Input:
//    videoStepProgress
//    userState.goalsProgress[goalId]?.do
function doStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) =>
    videoStepProgress(goalId, 'do', userId, revisionId, homeworkId)(userState);
}

// Returns: Number<Range[0, 1]>
// Input:
// curriculum
//    [practice]
function practiceStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const state = useCurriculumStore.getState();
    const practiceProgress = state.practice[goalId];

    if (!practiceProgress) return 0;

    const { totalCount, maxScore } = practiceProgress;

    if (totalCount === 0) return 0;
    if (maxScore === 5) return 1;
    return 0.5;
  };
}

// Returns: Number<Range[0, 1]>
// Input: userState.goalsProgress[goalId]?.quiz?.quizAttempts
function quizStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const { numCompletedQuizzes = 0, topScore = 0 } = getStepProgress(
      userState,
      goalId,
      userId,
      'quiz',
      revisionId,
      homeworkId
    );

    if (numCompletedQuizzes === 0) return 0;

    return Math.min(1, topScore / 100);
  };
}

// Returns: Number<Range[0, 1]>
// Input: userState.goalsProgress[goalId]?.remember
function rememberStepProgress(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const { numCompletedQuizzes = 0, rememberMarksAchieved = 0 } =
      getStepProgress(
        userState,
        goalId,
        userId,
        'remember',
        revisionId,
        homeworkId
      );

    if (numCompletedQuizzes === 0) return 0;

    return Math.min(
      1,
      rememberMarksAchieved / REMEMBER_CONFIG.NUM_GOAL_QUESTIONS
    );
  };
}

// Returns: { quizInProgress, numCompletedQuizzes, lastScore, topScore } ** N.B. Scores given as % **
// Input: userState.goalsProgress[goalId]?.quiz?.quizAttempts
function quizStepState(goalId, step, revisionId, homeworkId) {
  return (userState) =>
    getStepProgress(userState, goalId, undefined, step, revisionId, homeworkId);
}

function goalIsNotReady(goalId, userId) {
  return (userState) => {
    const state = useCurriculumStore.getState();
    const readinessObjectives = state.goals[goalId]?.readinessObjectives ?? [];
    const readinessObjInFocusFive = readinessObjectives.filter(({ id }) =>
      state.focusFive.goalIds.includes(id)
    );

    return readinessObjInFocusFive.some(
      (obj) => !goalIsCompleted(obj.id)(userState)
    );
  };
}

// Returns: Boolean
// Input:
//    each step progress
//    userState.goalsProgress[goalId]
function goalIsStarted(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    const goalProgress = getGoalProgress(
      userState,
      goalId,
      userId,
      revisionId,
      homeworkId
    );
    if (!Object.keys(goalProgress).length) return false;
    return !!(
      myGoalStepProgress(goalId, userId, revisionId, homeworkId)(userState) ||
      readinessStepProgress(
        goalId,
        userId,
        revisionId,
        homeworkId
      )(userState) ||
      learnStepProgress(goalId, userId, revisionId, homeworkId)(userState) ||
      doStepProgress(goalId, userId, revisionId, homeworkId)(userState) ||
      quizStepProgress(goalId, userId, revisionId, homeworkId)(userState) ||
      rememberStepProgress(goalId, userId, revisionId, homeworkId)(userState)
    );
  };
}

function courseIsStarted(goalIds, homeworkId) {
  return (userState) =>
    goalIds.some((id) =>
      goalIsStarted(id, undefined, undefined, homeworkId)(userState)
    );
}

function revisionIsStarted(goalIds, revisionId) {
  return (userState) =>
    goalIds.some((id) => goalIsStarted(id, undefined, revisionId)(userState));
}

function homeworkIsStarted(homework) {
  return (userState) => {
    const { courses, arbitraryGoals, memoryBoost, focusFive, tasksToInclude } =
      homework;

    return (
      (tasksToInclude.courses &&
        courses.some((course) => course?.proportionComplete > 0)) ||
      (tasksToInclude.arbitraryGoals &&
        arbitraryGoals.some((goal) => goal?.proportionComplete > 0)) ||
      (tasksToInclude.memoryBoost && memoryBoost?.proportionComplete > 0) ||
      (tasksToInclude.focusFive && focusFive?.proportionComplete > 0)
    );
  };
}

function courseIsCompleted(goalIds, homeworkId) {
  return (userState) =>
    goalIds.every((id) =>
      goalIsCompleted(id, undefined, undefined, homeworkId)(userState)
    );
}

function revisionIsCompleted(goalIds, revisionId) {
  return (userState) =>
    goalIds.every((id) =>
      goalIsCompleted(id, undefined, revisionId)(userState)
    );
}

function homeworkIsCompleted(homework) {
  return (userState) => {
    const { courses, arbitraryGoals, memoryBoost, focusFive, tasksToInclude } =
      homework;

    return (
      (!tasksToInclude.courses ||
        courses.every((course) => course.proportionComplete === 1)) &&
      (!tasksToInclude.arbitraryGoals ||
        arbitraryGoals.every((goal) => goal.proportionComplete === 1)) &&
      (!tasksToInclude.memoryBoost || memoryBoost?.proportionComplete === 1) &&
      (!tasksToInclude.focusFive || focusFive?.proportionComplete === 1)
    );
  };
}

// returns: percentage goals complete to max 2d.p.
function courseProgress(goalIds) {
  return (userState) => {
    const numGoalsCompleted = goalIds.filter((id) =>
      goalIsCompleted(id)(userState)
    ).length;

    const proportion = numGoalsCompleted / goalIds.length;
    const pcMax2dp = Math.round(10_000 * proportion) / 100;
    return pcMax2dp;
  };
}

// returns: percentage goals complete to max 2d.p.
function revisionProgress(revisionId) {
  const { revisionGoals = [] } =
    useCurriculumStore.getState().getRevision(revisionId) ?? {};
  return (userState) => {
    const numGoalsCompleted = revisionGoals.filter((goal) =>
      goalIsCompleted(goal.goalId, null, revisionId)(userState)
    ).length;

    const proportion = numGoalsCompleted / revisionGoals.length;
    const pcMax2dp = Math.round(10_000 * proportion) / 100;
    return pcMax2dp;
  };
}

function homeworkProgress(homeworkId) {
  const homework = useCurriculumStore.getState().getHomework(homeworkId);
  return (userState) => {
    if (!homework) return 0;

    const {
      courses,
      arbitraryGoals,
      memoryBoost,
      focusFive,
      numFocusFiveGoals,
      numMemoryBoosts,
      tasksToInclude,
    } = homework;

    const round = (n) => Math.floor(n + 0.5);

    const taskTypes = [
      {
        enabled: tasksToInclude.courses,
        total: () => courses.reduce((sum, c) => sum + c.numberOfGoals, 0),
        completed: () =>
          courses.reduce(
            (sum, c) =>
              sum + round(c.numberOfGoals * (c.proportionComplete ?? 0)),
            0
          ),
      },
      {
        enabled: tasksToInclude.arbitraryGoals,
        total: () => arbitraryGoals.length,
        completed: () => arbitraryGoals.filter((g) => g.isComplete).length,
      },
      {
        enabled: tasksToInclude.focusFive && focusFive,
        total: () => numFocusFiveGoals,
        completed: () =>
          round(focusFive.numToDo * focusFive.proportionComplete),
      },
      {
        enabled: tasksToInclude.memoryBoost && memoryBoost,
        total: () => numMemoryBoosts,
        completed: () =>
          round(memoryBoost.numToDo * memoryBoost.proportionComplete),
      },
    ];

    const { totalTasks, completedTasks } = taskTypes.reduce(
      (acc, t) =>
        t.enabled
          ? {
              totalTasks: acc.totalTasks + t.total(),
              completedTasks: acc.completedTasks + t.completed(),
            }
          : acc,
      { totalTasks: 0, completedTasks: 0 }
    );

    if (totalTasks === 0) return 0;

    const proportion = completedTasks / totalTasks;
    return Math.round(proportion * 10_000) / 100; // max 2dp
  };
}

// Returns: Boolean
// Input:
//    each step progress
//    userState.goalsProgress[goalId]
function goalIsCompleted(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    // If we already have a completion date the goal must be completed.
    // We probably don't even need the subsequent code that checks the
    // individual step progreess any more?
    const goalCompletionDate = userState.completedGoals?.[goalId];
    if (goalCompletionDate && !revisionId && !homeworkId) return true;

    const quizProgress = getStepProgress(
      userState,
      goalId,
      userId,
      'quiz',
      revisionId,
      homeworkId
    );

    if (quizProgress.topScore === 100) return true;
    if (
      myGoalStepProgress(goalId, userId, revisionId, homeworkId)(userState) !==
      1
    )
      return false;
    if (
      learnStepProgress(goalId, userId, revisionId, homeworkId)(userState) !== 1
    )
      return false;
    if (doStepProgress(goalId, userId, revisionId, homeworkId)(userState) !== 1)
      return false;
    return (
      quizStepProgress(goalId, userId, revisionId, homeworkId)(userState) === 1
    );
  };
}

function stepIsComplete(goalId, step, revisionId, homeworkId) {
  return (userState) => {
    switch (step) {
      case 'myGoal':
        return (
          myGoalStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'readiness':
        return (
          readinessStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'learn':
        return (
          learnStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'do':
        return (
          doStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'practice':
        return (
          practiceStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'quiz':
        return (
          quizStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      case 'remember':
        return (
          rememberStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) === 1
        );
      default:
        return false;
    }
  };
}

function stepIsStarted(goalId, step, revisionId, homeworkId) {
  return (userState) => {
    switch (step) {
      case 'myGoal':
        return (
          myGoalStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) > 0
        );
      case 'readiness':
        return (
          readinessStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) > 0
        );
      case 'learn':
        return (
          learnStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) > 0
        );
      case 'do':
        return (
          doStepProgress(goalId, undefined, revisionId, homeworkId)(userState) >
          0
        );
      case 'quiz':
        return (
          quizStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) > 0
        );
      case 'remember':
        return (
          rememberStepProgress(
            goalId,
            undefined,
            revisionId,
            homeworkId
          )(userState) > 0
        );
      default:
        return false;
    }
  };
}

function stepProgress(goalId, step, revisionId, homeworkId) {
  return (userState) => {
    switch (step) {
      case 'myGoal':
        return myGoalStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'readiness':
        return readinessStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'learn':
        return learnStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'do':
        return doStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'practice':
        return practiceStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'quiz':
        return quizStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      case 'remember':
        return rememberStepProgress(
          goalId,
          undefined,
          revisionId,
          homeworkId
        )(userState);
      default:
        return false;
    }
  };
}

function goalStepsCompleted(goalId, hasGoal, revisionId, homeworkId) {
  return (userState) => {
    if (!hasGoal) return null;

    const steps = ['readiness', 'learn', 'do', 'practice', 'quiz'];

    const hasRememberStep =
      useCurriculumStore.getState().goals[goalId]?.canRemember;
    const hasReadinessStep = useCurriculumStore
      .getState()
      .shouldShowReadiness(goalId);

    if (hasRememberStep) steps.push('remember');
    if (!hasReadinessStep) {
      const ix = steps.indexOf('readiness');
      if (ix !== -1) steps.splice(ix, 1);
    }

    const result = Object.fromEntries(
      steps.map((step) => [
        step,
        stepIsComplete(goalId, step, revisionId, homeworkId)(userState),
      ])
    );

    return result;
  };
}

function goalStepsProgress(goalId, hasGoal, revisionId, homeworkId) {
  return (userState) => {
    if (!hasGoal) return null;

    const steps = ['readiness', 'learn', 'do', 'practice', 'quiz'];

    const hasRememberStep =
      useCurriculumStore.getState().goals[goalId]?.canRemember;
    const hasReadinessStep = useCurriculumStore
      .getState()
      .shouldShowReadiness(goalId);

    if (hasRememberStep) steps.push('remember');
    if (!hasReadinessStep) {
      const ix = steps.indexOf('readiness');
      if (ix !== -1) steps.splice(ix, 1);
    }

    const result = Object.fromEntries(
      steps.map((step) => [
        step,
        stepProgress(goalId, step, revisionId, homeworkId)(userState),
      ])
    );

    return result;
  };
}

// Returns: one of ['not_started', 'not_ready', 'in_progress', 'completed']
// Input:
//    goalIsCompleted
//    goalIsStarted
//    goalIsReady
//    userState.goalsProgress[goalId]
function goalStatus(goalId, userId, revisionId, homeworkId) {
  return (userState) => {
    switch (true) {
      case goalIsCompleted(goalId, userId, revisionId, homeworkId)(userState):
        return 'completed';
      case goalIsNotReady(goalId, userId, revisionId, homeworkId)(userState):
        return 'not_ready';
      case goalIsStarted(goalId, userId, revisionId, homeworkId)(userState):
        return 'in_progress';
      default:
        return 'not_started';
    }
  };
}

function revisionStatus(revisionId, userId) {
  const { revisionGoals = [] } =
    useCurriculumStore.getState().getRevision(revisionId) ?? {};
  return (userState) => {
    switch (true) {
      case revisionIsCompleted(
        revisionGoals.map((goal) => goal.goalId),
        revisionId,
        userId
      )(userState):
        return 'completed';
      case revisionIsStarted(
        revisionGoals.map((goal) => goal.goalId),
        revisionId,
        userId
      )(userState):
        return 'in_progress';
      default:
        return 'not_started';
    }
  };
}

function homeworkStatus(homeworkId, userId) {
  const homework = useCurriculumStore.getState().getHomework(homeworkId);
  return (userState) => {
    switch (true) {
      case homeworkIsCompleted(homework, userId)(userState):
        return 'completed';
      case homeworkIsStarted(homework, userId)(userState):
        return 'in_progress';
      default:
        return 'not_started';
    }
  };
}

function goalStatuses(userId) {
  return (userState) => {
    const statuses = {};
    const goalIds = Object.keys(
      userId
        ? userState.pupilsGoalsProgress[userId]
        : userState.goalsProgress || {}
    );
    for (const goalId of goalIds) {
      const status = goalStatus(goalId, userId)(userState);
      statuses[goalId] = status;
    }
    return statuses;
  };
}

function getGoalStatusesDict(goalIds) {
  return (userState) => {
    return Object.fromEntries(
      goalIds.map((id) => [id, goalStatus(id)(userState)])
    );
  };
}

function pupilsEncounteredGoals(pupils) {
  return (userState) => {
    let encountered = {};
    pupils.forEach((pupil) => {
      const pupilEncountered = findEncounteredGoals(pupil.id)(userState);
      encountered = { ...encountered, ...pupilEncountered };
    });
    return encountered;
  };
}

function findEncounteredGoals(userId) {
  return (userState) => {
    const encountered = {};
    const goalsProgress = userId
      ? userState.pupilsGoalsProgress[userId]
      : userState.goalsProgress;
    const { diagnosticProgress } = userState;
    const { diagnosticQuizAttempt } = useCurriculumStore.getState();

    if (goalsProgress) {
      // First add any goals encountered in readiness and remember quizzes
      Object.values(goalsProgress).forEach((goal) => {
        [goal.quiz, goal.readiness, goal.remember].forEach((quiz) => {
          const { quizAttempts } = quiz;
          if (quizAttempts) {
            Object.values(quizAttempts).forEach((quizAttempt) => {
              quizAttempt.answers?.forEach((answer) => {
                encountered[answer.goalId] = {
                  goalId: answer.goalId,
                  status: 'encountered',
                  date: mostRecentDate([
                    quiz.lastNav,
                    encountered[answer.goalId]?.date,
                  ]),
                };
              });
            });
          }
        });
      });
    }

    if (diagnosticProgress && diagnosticProgress.quizAttempts) {
      // Now add any goals encountered in diagnostic quizzes
      Object.values(diagnosticProgress.quizAttempts).forEach((quizAttempt) => {
        quizAttempt.answers?.forEach((answer) => {
          encountered[answer.goalId] = {
            goalId: answer.goalId,
            status: 'encountered',
            date: mostRecentDate([
              quizAttempt.creationDate,
              encountered[answer.goalId]?.date,
            ]),
          };
        });
      });
    }

    if (diagnosticQuizAttempt) {
      diagnosticQuizAttempt.answers?.forEach((answer) => {
        encountered[answer.goalId] = {
          goalId: answer.goalId,
          status: 'encountered',
          date: mostRecentDate([
            diagnosticQuizAttempt.creationDate,
            encountered[answer.goalId]?.date,
          ]),
        };
      });
    }

    if (goalsProgress) {
      // Finally add the root goals from goalsProgress
      Object.values(goalsProgress).forEach((goal) => {
        encountered[goal.goalId] = {
          goalId: goal.goalId,
          status: goalStatus(goal.goalId, userId)(userState),
          date: mostRecentDate([
            goal.do.lastNav,
            goal.learn.lastNav,
            goal.myGoal.lastNav,
            encountered[goal.goalId]?.date,
          ]),
        };
      });
    }

    return encountered;
  };
}

// Returns: Boolean
// Input:
//    curriculum
//    [goalIsCompleted]
function ideaIsCompleted(ideaId, homeworkId) {
  return (userState) => {
    const { goalIds } = useCurriculumStore.getState().ideas[ideaId];
    return goalIds.every((id) =>
      goalIsCompleted(id, undefined, undefined, homeworkId)(userState)
    );
  };
}

// Returns: one of ['not_started', 'in_progress', 'completed']
// Input:
//    ideaIsCompleted
//    [goalIsStarted]
function ideaStatus(ideaId, homeworkId) {
  return (userState) => {
    if (ideaIsCompleted(ideaId, homeworkId)(userState)) return 'completed';

    const { goalIds } = useCurriculumStore.getState().ideas[ideaId];
    return goalIds.some((id) =>
      goalIsStarted(id, undefined, undefined, homeworkId)(userState)
    )
      ? 'in_progress'
      : 'not_started';
  };
}

function elementIsCompleted(elementType, elementId, revisonId, homeworkId) {
  return (userState) => {
    switch (elementType) {
      case 'idea':
        return ideaIsCompleted(elementId, homeworkId)(userState);
      case 'goal':
        return goalIsCompleted(
          elementId,
          undefined,
          revisonId,
          homeworkId
        )(userState);
      default:
        throw new Error('Unexpected elementType');
    }
  };
}

function elementStatus(elementType, elementId, revisonId, homeworkId) {
  return (userState) => {
    switch (elementType) {
      case 'idea':
        return ideaStatus(elementId, homeworkId)(userState);
      case 'goal':
        return goalStatus(
          elementId,
          undefined,
          revisonId,
          homeworkId
        )(userState);
      case 'revision':
        return revisionStatus(elementId)(userState);
      case 'homework':
        return homeworkStatus(elementId)(userState);
      default:
        throw new Error('Unexpected elementType');
    }
  };
}

export {
  elementIsCompleted,
  elementStatus,
  revisionStatus,
  homeworkStatus,
  goalStepsCompleted,
  goalStepsProgress,
  getGoalCompletedStatuses,
  courseProgress,
  revisionProgress,
  homeworkProgress,
  courseIsStarted,
  revisionIsStarted,
  homeworkIsStarted,
  courseIsCompleted,
  revisionIsCompleted,
  homeworkIsCompleted,
  readinessStepProgress,
  quizStepState,
  readinessQuizAttempted,
  readinessQuizCompleted,
  videoProgress,
  getFirstIncompleteVideo,
  stepIsComplete,
  stepIsStarted,
  rememberStepProgress,
  goalStatuses,
  getGoalStatusesDict,
  pupilsEncounteredGoals,
};
